import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { 
  Shield, 
  Target, 
  FileText, 
  GraduationCap, 
  Users, 
  Award,
  CheckCircle,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const WhyPage = () => {
  const valueProps = [
    {
      icon: <Shield className="h-12 w-12 text-blue-600" />,
      title: "Risk-Based Approach",
      description: "We apply ICH Q9 principles to focus effort where it matters most, ensuring maximum compliance impact with optimal resource allocation.",
      benefits: [
        "Prioritized compliance efforts based on actual risk",
        "Cost-effective resource allocation", 
        "Regulatory confidence through scientific risk management",
        "Scalable frameworks that grow with your business"
      ]
    },
    {
      icon: <Target className="h-12 w-12 text-green-600" />,
      title: "Inspection Readiness",
      description: "Every system we design is built with regulatory inspection in mind, ensuring you're always prepared for unannounced visits.",
      benefits: [
        "Mock audit preparation and execution",
        "Inspector-ready documentation and processes",
        "Confident regulatory interactions",
        "Faster inspection closure with fewer observations"
      ]
    },
    {
      icon: <FileText className="h-12 w-12 text-purple-600" />,
      title: "Documentation Clarity",
      description: "Clear, concise documentation that your teams can actually use, reducing training time and compliance errors.",
      benefits: [
        "User-friendly procedures and work instructions",
        "Reduced training time for new employees",
        "Fewer compliance errors and deviations",
        "Streamlined document control and maintenance"
      ]
    },
    {
      icon: <GraduationCap className="h-12 w-12 text-orange-600" />,
      title: "Training Effectiveness",
      description: "Comprehensive training programs that build competency and create sustainable quality culture across your organization.",
      benefits: [
        "Role-based competency development",
        "Measurable training effectiveness",
        "Strong quality culture development",
        "Long-term sustainability and knowledge retention"
      ]
    },
    {
      icon: <Users className="h-12 w-12 text-indigo-600" />,
      title: "Cross-Functional Experience",
      description: "Unique perspective spanning Quality, Operations, and IT, enabling holistic solutions that work across departments.",
      benefits: [
        "Integrated solutions across business functions",
        "Reduced silos and improved communication",
        "Comprehensive system design and implementation",
        "Practical solutions that consider all stakeholders"
      ]
    },
    {
      icon: <Award className="h-12 w-12 text-red-600" />,
      title: "Proven Track Record",
      description: "36+ years of combined experience with 100% audit-ready project delivery and measurable compliance improvements.",
      benefits: [
        "100% audit-ready projects delivered to clients",
        "Average 30-50% reduction in documentation redundancy",
        "25% faster inspection readiness on average", 
        "Consistent delivery of measurable compliance outcomes"
      ]
    }
  ];

  const comparison = [
    {
      category: "Documentation Approach",
      goldPlating: "Excessive detail, multiple overlapping procedures, complex approval workflows",
      rightSizing: "Clear, concise procedures focused on critical requirements with streamlined approval"
    },
    {
      category: "Risk Management",
      goldPlating: "Uniform high-level controls regardless of actual risk level",
      rightSizing: "ICH Q9 risk-based approach with controls proportional to actual risk"
    },
    {
      category: "Training Programs",
      goldPlating: "Generic, lengthy training covering every possible scenario",
      rightSizing: "Role-specific, competency-based training focused on job requirements"
    },
    {
      category: "Change Control",
      goldPlating: "Complex approval chains for minor changes, lengthy review cycles",
      rightSizing: "Risk-based change categories with appropriate approval levels"
    },
    {
      category: "Data Integrity",
      goldPlating: "Extensive manual reviews and paper-based verification systems",
      rightSizing: "Automated controls with exception-based review and electronic workflows"
    }
  ];

  const regulators = ["FDA", "MHRA", "EMA", "Health Canada", "TGA", "PMDA"];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-navy-900 mb-6">
              Why Choose <span className="text-blue-600">SP GxP Consultants</span>?
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              In a world of over-engineered compliance systems, we deliver practical solutions 
              that actually work. Here's what sets us apart from typical consulting approaches.
            </p>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Value Proposition</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Six key differentiators that ensure your compliance investment delivers 
              real business value and regulatory confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {valueProps.map((prop, index) => (
              <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-all duration-300 h-full">
                <CardHeader>
                  <div className="mb-4">
                    {prop.icon}
                  </div>
                  <CardTitle className="text-xl text-navy-900 mb-2">{prop.title}</CardTitle>
                  <p className="text-gray-600">{prop.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {prop.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Gold-Plating vs <span className="text-blue-600">Right-Sizing</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the difference between over-engineered compliance systems and 
              our practical, sustainable approach.
            </p>
          </div>

          <Card className="shadow-xl border-0">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-navy-900 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left">Category</th>
                      <th className="px-6 py-4 text-left">
                        <div className="flex items-center space-x-2">
                          <X className="h-5 w-5 text-red-400" />
                          <span>Gold-Plating Approach</span>
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          <span>Our Right-Sizing Approach</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-6 py-4 font-semibold text-navy-900">{item.category}</td>
                        <td className="px-6 py-4 text-gray-600">{item.goldPlating}</td>
                        <td className="px-6 py-4 text-gray-600">{item.rightSizing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Regulatory Context */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Global <span className="text-blue-600">Regulatory Experience</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Our team has successfully supported inspections and compliance programs 
              across major regulatory jurisdictions worldwide.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {regulators.map((regulator, index) => (
                <div 
                  key={index}
                  className="px-6 py-3 bg-blue-50 text-blue-700 rounded-full font-semibold border-2 border-blue-200"
                >
                  {regulator}
                </div>
              ))}
            </div>
            
            <p className="text-gray-600 mt-6 italic">
              * Listed for context of experience - not endorsements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <Card className="text-center shadow-lg border-0">
              <CardContent className="p-8">
                <div className="text-3xl font-bold text-blue-600 mb-2">36+</div>
                <div className="text-lg font-semibold text-navy-900 mb-2">Years Combined Experience</div>
                <div className="text-gray-600">in GMP & GxP programs</div>
              </CardContent>
            </Card>
            
            <Card className="text-center shadow-lg border-0">
              <CardContent className="p-8">
                <div className="text-3xl font-bold text-green-600 mb-2">100%</div>
                <div className="text-lg font-semibold text-navy-900 mb-2">Audit-Ready Projects</div>
                <div className="text-gray-600">handed over to clients</div>
              </CardContent>
            </Card>
            
            <Card className="text-center shadow-lg border-0">
              <CardContent className="p-8">
                <div className="text-3xl font-bold text-purple-600 mb-2">25%</div>
                <div className="text-lg font-semibold text-navy-900 mb-2">Faster Inspection Readiness</div>
                <div className="text-gray-600">average improvement</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};